import AjaxWishlistActionBase from './AjaxWishlistActionBase';
import decodeEntities from '@mvps-genesis/decode-entities';
import EventBus from '@mvps-genesis/event-bus';

/*
 *--------------------------------------------------------------------------
 * AjaxAddToWishlist
 *--------------------------------------------------------------------------
 *
 * Functionality for asynchronously handling Miva Add To Wishlist (ATWL)
 * actions.
 *
 */
export default class AjaxAddToWishlist extends AjaxWishlistActionBase {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * The add to wishlist form element
	 *
	 * @var {HTMLElement}
	 */
	formElement;

	/**
	 * The container element of the wishlist action elements
	 *
	 * @var	{HTMLElement}
	 */
	actionsContainerElement;

	/**
	 * The collection of wishlist action elements
	 *
	 * @var	{HTMLCollection}
	 */
	actionElements;

	/**
	 * Mapping of attribute names for action and action container elements
	 *
	 * @var	{Object}
	 */
	actionAttributes;

	/**
	 * The add to wishlist page url; this will typically be the WISH page
	 *
	 * @var {String}
	 */
	actionUrl;

	/**
	 * Create a new AjaxAddToWishlist object.
	 *
	 * @param  {HTMLElement|String}  formElement                      The form element.
	 * @param  {String}              options.actionElementClass       The action element class name. Defaults to 'js-add-to-wishlist'.
	 * @param  {Object}              options.actionInput              The action input object. Defaults to name = Action, value = ATWL.
	 * @param  {HTMLElement|String}  options.actionsContainerElement  The container element of the wishlist action elements. Defaults to '.g-add-to-wishlist-dialog__wishlists'.
	 * @param  {HTMLElement|String}  options.loadingElement           The loading UI element. Defaults to '#js-loading-container'.
	 * @param  {HTMLElement|String}  options.loadingOverlayElement    The loading UI overlay element. Defaults to '#js-wishlist-container'.
	 * @param  {HTMLElement|String}  options.messageElement           The message container element. Defaults to '#js-add-to-wishlist-message'.
	 * @param  {Object}              options.messageOptions           The message configuration options object.
	 * @param  {String}              options.wishlistIdAttribute      The wishlist id attribute, must be present on an action element. Defaults to 'data-wishlist-id'.
	 * @param  {String}              options.wishlistListsAttribute   The wishlists lists data attribute. This triggers the dynamic creation of the action elements when present and contains a value on the actionsContainerElement. The attribute value should be in a JSON list format with each object containing the wishlist id and wishlist title for each action element. Defaults to 'data-wishlist-lists'.
	 * @param  {String}              options.wishlistTitleAttribute   The wishlist title attribute, must be present on an action element. Defaults to 'data-wishlist-title'.
	 * @param  {String}              options.wishlistUrlAttribute     The add to wishlist page url, typically the WISH page. This attribute must be present on the form element. If omitted, the form element action attribute will be used for the add to wishlist page url. Defaults to 'data-wishlist-url'.
	 */
	// eslint-disable-next-line max-lines-per-function
	constructor (formElement, {
		actionElementClass = 'js-add-to-wishlist',
		actionInput = {
			name: 'Action',
			value: 'ATWL'
		},
		actionsContainerElement = '.g-add-to-wishlist-dialog__wishlists',
		loadingElement = '#js-loading-container',
		loadingOverlayElement = '#js-wishlist-container',
		messageElement = '#js-add-to-wishlist-message',
		messageOptions = {
			error: {
				dynamic: true,
				message: 'Unable to add to list <strong>%wishlistTitle%</strong>.',
				options: {
					autohide: false,
					type: 'error'
				},
				selector: '.g-messages--error'
			},
			success: {
				message: 'Successfully added to list <strong>%wishlistTitle%</strong>.',
				options: {
					autohide: false,
					type: 'success'
				},
				selector: '.g-messages--success'
			}
		},
		wishlistIdAttribute = 'data-wishlist-id',
		wishlistListsAttribute = 'data-wishlist-lists',
		wishlistTitleAttribute = 'data-wishlist-title',
		wishlistUrlAttribute = 'data-wishlist-url'
	} = {}) {
		super({
			actionInput,
			loadingElement,
			loadingOverlayElement,
			messageElement,
			messageOptions
		});

		this.#validator = this.getValidator();

		this.formElement = this.#validator.isElement('formElement', formElement);

		this.actionsContainerElement = this.#validator.isElement('actionsContainerElement', actionsContainerElement);

		this.actionAttributes = {
			actionElementClass: this.#validator.isString('actionElementClass', actionElementClass),
			wishlistIdAttribute: this.#validator.isString('wishlistIdAttribute', wishlistIdAttribute),
			wishlistListsAttribute: this.#validator.isString('wishlistListsAttribute', wishlistListsAttribute),
			wishlistTitleAttribute: this.#validator.isString('wishlistTitleAttribute', wishlistTitleAttribute),
			wishlistUrlAttribute: this.#validator.isString('wishlistUrlAttribute', wishlistUrlAttribute)
		};

		this.actionUrl = this.#validator.isUrl('actionUrl', this.formElement.getAttribute(this.actionAttributes.wishlistUrlAttribute) || this.formElement.action);

		this.createActionElements();

		this.actionElements = this.#validator.isElementList('actionElements', this.actionsContainerElement.getElementsByClassName(this.actionAttributes.actionElementClass));

		this.bindEvents();
	}

	/**
	 * Bind element events.
	 */
	bindEvents () {
		for (const element of this.actionElements) {
			this.bindActionElementEvents(element);
		}
	}

	/**
	 * Bind action element events.
	 *
	 * @param {HTMLElement} actionElement The action element.
	 */
	bindActionElementEvents (actionElement) {
		actionElement.addEventListener('click', this.onActionElementClick.bind(this));
	}

	/**
	 * Dynamically create the action elements and append to the actions container element.
	 *
	 * @throws {TypeError} Will throw an error if an invalid wishlist lists attribute value is provided.
	 */
	createActionElements () {
		const wishlistsAttributeValue = this.actionsContainerElement.getAttribute(this.actionAttributes.wishlistListsAttribute);

		if (!wishlistsAttributeValue) {
			return;
		}

		const wishlists = JSON.parse(decodeEntities(wishlistsAttributeValue));

		if (!wishlists) {
			throw new TypeError(`${this.constructor.name} :: Invalid ${this.actionAttributes.wishlistListsAttribute} attribute value provided`);
		}

		let wishlistsFragment = document.createDocumentFragment();

		wishlists.forEach((wishlist) => {
			wishlistsFragment.appendChild(this.createActionHtmlElement(wishlist));
		});

		this.appendActionElement(wishlistsFragment);
	}

	/**
	 * Create an action element and bind the events.
	 *
	 * @param   {Object} wishlist The wishlist data to generate the action element.
	 * @returns {HTMLElement}     The created action element.
	 */
	createActionElement (wishlist) {
		const actionElement = this.createActionHtmlElement(wishlist);

		this.bindActionElementEvents(actionElement);

		return actionElement;
	}

	/**
	 * Create an action element.
	 *
	 * @param   {Object} wishlist The wishlist data to generate the action element.
	 * @returns {HTMLElement}     The created action element.
	 */
	createActionHtmlElement (wishlist) {
		const containerElement = document.createElement('button');

		containerElement.classList.add(this.actionAttributes.actionElementClass, 'g-add-to-wishlist-dialog__wishlist', 'g-button--no-styling');
		containerElement.setAttribute(this.actionAttributes.wishlistIdAttribute, wishlist.id);
		containerElement.setAttribute(this.actionAttributes.wishlistTitleAttribute, wishlist.title);

		const titleElement = document.createElement('span');

		titleElement.classList.add('g-add-to-wishlist-dialog__wishlist-title');
		titleElement.textContent = wishlist.title;

		const actionContainerElement = document.createElement('span');

		actionContainerElement.classList.add('g-add-to-wishlist-dialog__wishlist-action');

		const actionIconElement = document.createElement('span');

		actionIconElement.classList.add('g-add-to-wishlist-dialog__wishlist-action-icon', 'u-icon-add-circle');
		actionIconElement.setAttribute('aria-hidden', 'true');

		const actionTextElement = document.createElement('span');

		actionTextElement.classList.add('u-hide-visually');
		actionTextElement.textContent = `Add this product to list ${wishlist.title}.`;

		actionContainerElement.appendChild(actionIconElement);
		actionContainerElement.appendChild(actionTextElement);

		containerElement.appendChild(titleElement);
		containerElement.appendChild(actionContainerElement);

		return containerElement;
	}

	/**
	 * Append an action element to the action container element.
	 *
	 * @param {HTMLElement} actionElement The action element.
	 */
	appendActionElement (actionElement) {
		this.actionsContainerElement.appendChild(actionElement);
	}

	/**
	 * Handle action element click event. This submits the wishlist form.
	 *
	 * @async
	 * @param {Event} event The click event.
	 */
	// eslint-disable-next-line max-lines-per-function
	async onActionElementClick (event) {
		const actionElement = event.currentTarget;
		const wishlistId = actionElement.getAttribute(this.actionAttributes.wishlistIdAttribute);
		const wishlistTitle = actionElement.getAttribute(this.actionAttributes.wishlistTitleAttribute);

		if (!wishlistId || !wishlistTitle) {
			return;
		}

		event.preventDefault();

		const formData = new FormData(this.formElement);

		// Set wishlist action and id input params
		formData.set(this.actionInput.name, this.actionInput.value);
		formData.set('WishList_ID', wishlistId);

		const request = new Request(this.actionUrl, {
			body: formData,
			method: this.formElement.method || 'POST'
		});

		let eventDetail = {
			data: {},
			form: this.formElement,
			formData: formData,
			message: '',
			request: request,
			success: false
		};

		actionElement.disabled = true;
		this.messageContainer.clear();

		if (this.loadingUI) {
			this.loadingUI.show();
		}

		const response = await fetch(request);

		if (response.ok) {
			const responseHtml = await response.text();
			const responseDom = new DOMParser().parseFromString(responseHtml, 'text/html');
			const pageCodeResponseHeader = response.headers.get('g-mm-page-code');

			eventDetail.data = responseDom;

			switch (pageCodeResponseHeader) {
				case 'PROD': {
					this.onMissingAttributes(responseDom, wishlistTitle);

					break;
				}

				case 'WISH': {
					const errorMessageElement = responseDom.querySelector(this.messageOptions.error.selector);

					if (errorMessageElement) {
						this.onFail(responseDom, wishlistTitle, errorMessageElement);
					} else {
						this.onSuccess(responseDom, wishlistTitle);

						eventDetail.success = true;
					}

					break;
				}

				default: {
					this.onFail(responseDom, wishlistTitle);
				}
			}

			eventDetail.message = this.messageContainer.currentMessage.message;
		} else {
			this.onFail({}, wishlistTitle);

			eventDetail.message = `${this.constructor.name} - The request failed with a status of: ${response.status}`;
		}

		actionElement.disabled = false;

		if (this.loadingUI) {
			this.loadingUI.hide();
		}

		// Dispatch global insertWishlist event
		EventBus.dispatchEvent('MVPS:addToWishlist', eventDetail);
	}

	/**
	 * Handle the missing attributes response.
	 *
	 * @param {HTMLDocument} response      The response object.
	 * @param {String}       wishlistTitle The wishlist title.
	 */
	onMissingAttributes (response, wishlistTitle) {
		const missingAttributes = [];
		const findRequired = this.formElement.querySelectorAll('.g-product-layout-purchase__options .is-required');

		for (let i = 0, len = findRequired.length; i < len; i++) {
			missingAttributes.push((findRequired[i].title ? findRequired[i].title : findRequired[i].textContent).replace(/:$/, ''));
		}

		const errorMessageHtml = 'All <strong>Required</strong> options have not been selected.<br>Please review the following options: <strong class="u-color-error">%missingAttributes%</strong>';
		const errorMessageElement = document.createElement('div');

		errorMessageElement.innerHTML = errorMessageHtml.replace('%missingAttributes%', missingAttributes.join(', '));

		this.onFail(response, wishlistTitle, errorMessageElement);
	}

	/**
	 * Handle successful add to wishlist response.
	 *
	 * @param {HTMLDocument} response      The response object.
	 * @param {String}       wishlistTitle The wishlist title.
	 */
	onSuccess (response, wishlistTitle) {
		this.createMessage(this.messageOptions.success.message.replace('%wishlistTitle%', wishlistTitle), this.messageOptions.success.options);
	}

	/**
	 * Handle failed add to wishlist response.
	 *
	 * @param {HTMLDocument} response                   The response object.
	 * @param {String}       wishlistTitle              The wishlist title.
	 * @param {HTMLElement}  [errorMessageElement=null] The error message element.
	 */
	onFail (response, wishlistTitle, errorMessageElement = null) {
		let messageElement = null;
		let errorMessage = this.messageOptions.error.message.replace('%wishlistTitle%', wishlistTitle);

		if (errorMessageElement instanceof HTMLElement) {
			messageElement = errorMessageElement;
		} else if (this.messageOptions.error.dynamic) {
			messageElement = response.querySelector(this.messageOptions.error.selector) ?? null;
		}

		if (messageElement) {
			errorMessage = messageElement.innerHTML;
		}

		this.createMessage(errorMessage, this.messageOptions.error.options);
	}

};
